import React from 'react';
import Icon from 'components/AppIcon';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';

export const SUBSCRIPTION_TIERS = {
  free: {
    id: 'free',
    name: 'Free',
    color: '#6B7280',
    icon: 'User',
    durations: { simple: '30s', medium: '60s', complex: '120s' },
    maxDuration: 120,
    badge: null,
  },
  pro: {
    id: 'pro',
    name: 'Pro',
    color: '#6366F1',
    icon: 'Zap',
    durations: { simple: '5 min', medium: '5 min', complex: '5 min' },
    maxDuration: 300,
    badge: '⚡ Pro',
  },
  studio: {
    id: 'studio',
    name: 'Studio',
    color: '#F59E0B',
    icon: 'Star',
    durations: { simple: '10 min', medium: '10 min', complex: '10 min' },
    maxDuration: 600,
    badge: '🌟 Studio',
  },
  enterprise: {
    id: 'enterprise',
    name: 'Enterprise',
    color: '#10B981',
    icon: 'Building2',
    durations: { simple: 'Unlimited', medium: 'Unlimited', complex: 'Unlimited' },
    maxDuration: Infinity,
    badge: '🏢 Enterprise',
  },
};

export const TIER_DURATION_LABELS = {
  free: [
    { complexity: 'Simple', duration: '30 seconds', icon: 'Minus' },
    { complexity: 'Medium', duration: '60 seconds', icon: 'Equal' },
    { complexity: 'Complex', duration: '120 seconds', icon: 'Plus' },
  ],
  pro: [
    { complexity: 'Simple', duration: '5 minutes', icon: 'Minus' },
    { complexity: 'Medium', duration: '5 minutes', icon: 'Equal' },
    { complexity: 'Complex', duration: '5 minutes', icon: 'Plus' },
  ],
  studio: [
    { complexity: 'Simple', duration: '10 minutes', icon: 'Minus' },
    { complexity: 'Medium', duration: '10 minutes', icon: 'Equal' },
    { complexity: 'Complex', duration: '10 minutes', icon: 'Plus' },
  ],
  enterprise: [
    { complexity: 'Simple', duration: 'Unlimited', icon: 'Minus' },
    { complexity: 'Medium', duration: 'Unlimited', icon: 'Equal' },
    { complexity: 'Complex', duration: 'Unlimited', icon: 'Plus' },
  ],
};

export default function SubscriptionTierBadge({ tier = 'free', onUpgrade, compact = false }) {
  const navigate = useNavigate();
  const { isPlatformOwner } = useAuth();
  const tierData = SUBSCRIPTION_TIERS?.[tier] || SUBSCRIPTION_TIERS?.free;
  const durations = TIER_DURATION_LABELS?.[tier] || TIER_DURATION_LABELS?.free;

  const handleUpgrade = () => {
    if (onUpgrade) {
      onUpgrade();
    } else {
      navigate('/pricing');
    }
  };

  if (compact) {
    if (isPlatformOwner) {
      return (
        <div
          className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold"
          style={{
            background: 'linear-gradient(135deg, rgba(16,185,129,0.2), rgba(99,102,241,0.2))',
            border: '1px solid rgba(16,185,129,0.5)',
            color: '#10B981',
            fontFamily: 'var(--font-caption)',
          }}
        >
          <Icon name="ShieldCheck" size={11} color="#10B981" />
          Platform Owner
        </div>
      );
    }
    return (
      <div
        className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold cursor-pointer hover:opacity-80 transition-all"
        style={{ background: `${tierData?.color}18`, color: tierData?.color, border: `1px solid ${tierData?.color}40`, fontFamily: 'var(--font-caption)' }}
        onClick={handleUpgrade}
        title={`${tierData?.name} Plan — Click to upgrade`}
      >
        <Icon name={tierData?.icon} size={11} color={tierData?.color} />
        {tierData?.name}
      </div>
    );
  }

  return (
    <div
      className="rounded-2xl p-4 flex flex-col gap-3"
      style={{ background: isPlatformOwner ? 'rgba(16,185,129,0.06)' : `${tierData?.color}08`, border: `1px solid ${isPlatformOwner ? 'rgba(16,185,129,0.3)' : `${tierData?.color}30`}` }}
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ background: isPlatformOwner ? 'rgba(16,185,129,0.2)' : `${tierData?.color}20` }}
          >
            <Icon name={isPlatformOwner ? 'ShieldCheck' : tierData?.icon} size={16} color={isPlatformOwner ? '#10B981' : tierData?.color} />
          </div>
          <div>
            <p className="text-sm font-bold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>
              {isPlatformOwner ? 'Platform Owner Mode' : `${tierData?.name} Plan`}
            </p>
            <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>
              {isPlatformOwner ? 'Unlimited access · No billing' : 'Generation duration limits'}
            </p>
          </div>
        </div>
        {!isPlatformOwner && tier !== 'enterprise' && (
          <button
            onClick={handleUpgrade}
            className="text-xs px-3 py-1.5 rounded-lg font-semibold transition-all hover:opacity-80"
            style={{ background: tierData?.color, color: 'white', fontFamily: 'var(--font-caption)' }}
          >
            Upgrade
          </button>
        )}
      </div>
      {/* Duration grid */}
      <div className="grid grid-cols-3 gap-2">
        {durations?.map((d) => (
          <div
            key={d?.complexity}
            className="flex flex-col items-center gap-1 p-2 rounded-xl text-center"
            style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)' }}
          >
            <Icon name={d?.icon} size={12} color={isPlatformOwner ? '#10B981' : tierData?.color} />
            <span className="text-xs font-semibold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>
              {d?.complexity}
            </span>
            <span className="text-xs" style={{ color: isPlatformOwner ? '#10B981' : tierData?.color, fontFamily: 'var(--font-data)', fontWeight: 600 }}>
              {isPlatformOwner ? 'Unlimited' : d?.duration}
            </span>
          </div>
        ))}
      </div>
      {!isPlatformOwner && tier === 'free' && (
        <p className="text-xs text-center" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>
          Upgrade to Pro for up to 5-minute generations
        </p>
      )}
    </div>
  );
}
